import type { FC, ReactNode } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  ArrowPathIcon,
  CheckIcon,
  ClipboardDocumentIcon,
  ClockIcon,
  EnvelopeIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline"
import { useQueryClient } from "@tanstack/react-query"
import { isAxiosError } from "axios"

import { queryKeys } from "~/api/cache"
import type {
  ArcadiaGreenButtonConnectionDto,
  ArcadiaGreenButtonIntervalImportDto,
  ArcadiaGreenButtonMeterSelectionOptionDto,
  ArcadiaGreenButtonProposalApplicationDecision,
  ArcadiaGreenButtonProposalApplicationOutcome,
  ArcadiaGreenButtonProposalApplicationReason,
} from "~/api/dto/arcadia-green-button.dto"
import type { ConsumptionSelectionBlockedReason } from "~/api/dto/proposal-consumption.dto"
import useApplySolarProposalGreenButton from "~/api/hooks/useApplySolarProposalGreenButton"
import { useConfirmArcadiaGreenButtonUsage } from "~/api/hooks/useConfirmArcadiaGreenButtonUsage"
import { useFetchArcadiaGreenButtonSiteData } from "~/api/hooks/useFetchArcadiaGreenButtonSiteData"
import { useLinkArcadiaGreenButtonProject } from "~/api/hooks/useLinkArcadiaGreenButtonProject"
import useProposalConsumptionInsights from "~/api/hooks/useProposalConsumptionInsights"
import useProposalConsumptionSelection from "~/api/hooks/useProposalConsumptionSelection"
import { useResendArcadiaGreenButtonConnectionEmail } from "~/api/hooks/useResendArcadiaGreenButtonConnectionEmail"
import { useResumeArcadiaGreenButtonUsage } from "~/api/hooks/useResumeArcadiaGreenButtonUsage"
import { useRetryArcadiaGreenButtonConnection } from "~/api/hooks/useRetryArcadiaGreenButtonConnection"
import { useSelectArcadiaGreenButtonMeter } from "~/api/hooks/useSelectArcadiaGreenButtonMeter"
import { useStartArcadiaGreenButtonConnection } from "~/api/hooks/useStartArcadiaGreenButtonConnection"
import type { DrawerTheme } from "~/components/ui-components/v2"
import { Badge, Drawer } from "~/components/ui-components/v2"
import { parseAxiosError } from "~/utils/parseAxiosError"

import { GRID_IMPORT_CONNECTED_COPY } from "./consumption-source-labels"
import { ConsumptionSourceComparison } from "./ConsumptionSourceComparison"
import {
  SELECTION_ONLY_APPLIED_COPY,
  SELECTION_ONLY_PENDING_IMPORT_COPY,
  type GreenbuttonApplyMode,
} from "./greenbutton-apply-mode"
import {
  GREENBUTTON_DRAWER_TABS,
  GREENBUTTON_SUPPORT_TAB,
  type GreenbuttonDrawerTab,
  type GreenbuttonDrawerTabId,
  GreenbuttonDrawerTabBar,
} from "./greenbutton-drawer-tabs"
import {
  SolarFitEmptyState,
  SolarFitTab,
} from "./greenbutton-insights/SolarFitTab"
import {
  TypicalDayEmptyState,
  TypicalDayTab,
} from "./greenbutton-insights/TypicalDayTab"
import {
  UsagePatternsEmptyState,
  UsagePatternsTab,
} from "./greenbutton-insights/UsagePatternsTab"
import { GreenbuttonSupportDiagnostics } from "./GreenbuttonSupportDiagnostics"

const buildSteps = (homeownerName: string) => [
  {
    title: `We email the homeowner`,
    description: `${homeownerName} gets a secure link to connect their utility account.`,
  },
  {
    title: `They authorize their utility`,
    description: `One read-only sign-in through Arcadia's hosted flow.`,
  },
  {
    title: `Usage is captured for review`,
    description: `Real kWh appears here before it changes the proposal - no bills to upload.`,
  },
]

const HEADLINE = `Use the homeowner's real utility data`

type GreenbuttonDisplayStatus =
  | `captured`
  | `connecting`
  | `failed`
  | `not-started`
  | `requested`
  | `selection-required`
  | `synced`

const GREENBUTTON_STATUS_CONFIG: Record<
  GreenbuttonDisplayStatus,
  {
    description: string
    dotClassName: string
    label: string
    pillClassName: string
  }
> = {
  "captured": {
    description: `Usage is ready to review and has not changed this proposal`,
    dotClassName: `bg-success`,
    label: `Usage captured`,
    pillClassName: `border-success/20 bg-success/5 text-success`,
  },
  "connecting": {
    description: `Homeowner utility connection in progress`,
    dotClassName: `bg-primary`,
    label: `Connecting`,
    pillClassName: `border-primary/20 bg-primary/5 text-primary`,
  },
  "failed": {
    description: `Greenbutton data couldn't connect`,
    dotClassName: `bg-danger`,
    label: `Couldn't connect`,
    pillClassName: `border-danger/20 bg-danger/5 text-danger`,
  },
  "not-started": {
    description: `No Greenbutton data on this proposal yet`,
    dotClassName: `bg-muted`,
    label: `Not started`,
    pillClassName: `border-border bg-surface text-muted`,
  },
  "requested": {
    description: `Request sent to homeowner`,
    dotClassName: `bg-primary`,
    label: `Requested`,
    pillClassName: `border-primary/20 bg-primary/5 text-primary`,
  },
  "selection-required": {
    description: `Choose one electric meter to continue`,
    dotClassName: `bg-warning`,
    label: `Meter selection needed`,
    pillClassName: `border-warning/40 bg-warning/15 text-foreground`,
  },
  "synced": {
    description: `Green Button usage is applied to this proposal`,
    dotClassName: `bg-success`,
    label: `Synced`,
    pillClassName: `border-success/20 bg-success/5 text-success`,
  },
}

const REQUESTED_CONNECTION_STATUSES = new Set([`created`, `connect_started`])
const CONNECTING_CONNECTION_STATUSES = new Set([
  `credentials_submitted`,
  `discovering`,
  `activating_meter`,
  `intervals_pending`,
  `importing`,
])
const FAILED_CONNECTION_STATUSES = new Set([`failed`, `cancelled`, `expired`])
const CONNECTING_CONNECT_STATUSES = new Set([
  `connect_started`,
  `credentials_submitted`,
  `completed`,
])

const getGreenbuttonDisplayStatus = ({
  connection,
  currentImport,
  currentProposalApplicationOutcome,
  isCurrentProposalUsageLocked,
  hasMeterSelectionOptions,
}: {
  connection?: ArcadiaGreenButtonConnectionDto | null
  currentImport?: ArcadiaGreenButtonIntervalImportDto | null
  currentProposalApplicationOutcome?: ArcadiaGreenButtonProposalApplicationOutcome | null
  isCurrentProposalUsageLocked: boolean
  hasMeterSelectionOptions: boolean
}): GreenbuttonDisplayStatus => {
  if (!connection) {
    return `not-started`
  }

  if (
    currentProposalApplicationOutcome === `applied` &&
    !isCurrentProposalUsageLocked
  ) {
    return `synced`
  }

  if (
    connection.status === `imported` ||
    Boolean(connection.currentImportId) ||
    Boolean(connection.completedAt) ||
    Boolean(currentImport)
  ) {
    return `captured`
  }

  if (
    Boolean(connection.failedAt) ||
    connection.connectStatus === `failed` ||
    FAILED_CONNECTION_STATUSES.has(connection.status)
  ) {
    return `failed`
  }

  if (connection.status === `meter_selection_required`) {
    return hasMeterSelectionOptions ? `selection-required` : `connecting`
  }

  if (
    CONNECTING_CONNECTION_STATUSES.has(connection.status) ||
    CONNECTING_CONNECT_STATUSES.has(connection.connectStatus)
  ) {
    return `connecting`
  }

  if (
    connection.connectStatus === `connect_url_created` ||
    REQUESTED_CONNECTION_STATUSES.has(connection.status)
  ) {
    return `requested`
  }

  return `connecting`
}

type GreenbuttonDataDrawerProps = {
  /**
   * Set when the proposal's selections are locked (Solar V2's financing
   * lock): switch, resume and confirm are disabled with this reason, while
   * the Site-level connection actions stay available.
   */
  applyLockedReason?: string | null
  /** Which endpoints apply data to the proposal; see `GreenbuttonApplyMode`. */
  applyMode?: GreenbuttonApplyMode
  canUseSupportDiagnostics?: boolean
  /**
   * Battery-Only Proposals keep household consumption separate from measured
   * grid imports. When set, ineligible GRID_IMPORT data is explained instead
   * of offering a household-consumption apply action.
   */
  forBatteryOnlyProposal?: boolean
  /** Extra captured-usage content, such as Battery Savings Input. */
  afterCapturedUsage?: ReactNode
  homeownerEmail: string
  homeownerName: string
  onClose: () => void
  /** With `selection_only`: takes the seller to the savings Recalculate action. */
  onRecalculateSavings?: () => void
  onUsageApplied?: () => Promise<unknown>
  proposalId: string
  siteId: string
  /**
   * The surrounding proposal's palette. The drawer's own markup is entirely
   * semantic tokens, so Battery-Only gets its colors by asking for the dark
   * route theme rather than by forking the drawer.
   */
  theme?: DrawerTheme
  utilityCompanyName: string
}

const getResendEmailStatus = ({
  homeownerEmail,
  isError,
  isSuccess,
}: {
  homeownerEmail: string
  isError: boolean
  isSuccess: boolean
}): string | null => {
  if (isError) {
    return `Unable to resend email. Please try again.`
  }

  if (isSuccess) {
    return `Email resent to ${homeownerEmail}.`
  }

  return null
}

const getSendRequestLabel = ({
  isLoading,
  isSuccess,
}: {
  isLoading: boolean
  isSuccess: boolean
}): string => {
  if (isLoading) {
    return `Sending request...`
  }

  if (isSuccess) {
    return `Request sent to homeowner`
  }

  return `Send request to homeowner`
}

const getSendRequestStatus = ({
  hasProposal,
  hasSite,
  isError,
  isSuccess,
}: {
  hasProposal: boolean
  hasSite: boolean
  isError: boolean
  isSuccess: boolean
}): string | null => {
  if (!hasSite) {
    return `Site data is not ready yet.`
  }

  if (!hasProposal) {
    return `Proposal data is not ready yet.`
  }

  if (isError) {
    return `Unable to send request. Please try again.`
  }

  if (isSuccess) {
    return `Request created. The homeowner can use the secure connection link.`
  }

  return null
}

type GreenbuttonNotStartedBodyProps = {
  introCopy: string
  linkForm: ReactNode
  onStartLinking: () => void
  steps: ReturnType<typeof buildSteps>
}

export const LINK_PROJECT_HELP = `Enter the Powur project that already has Greenbutton Data. We'll use its connection here, so the homeowner doesn't have to authorize their utility again.`

type GreenbuttonLinkProjectFormProps = {
  errorMessage: string | null
  isLinking: boolean
  onCancel: () => void
  onSubmit: (powurProjectId: number) => void
}

const GreenbuttonLinkProjectForm: FC<GreenbuttonLinkProjectFormProps> = ({
  errorMessage,
  isLinking,
  onCancel,
  onSubmit,
}) => {
  const [projectId, setProjectId] = useState(``)
  const trimmed = projectId.trim()
  const parsed = Number(trimmed)
  const isValid = trimmed !== `` && Number.isInteger(parsed) && parsed > 0

  return (
    <form
      className="mt-8 max-w-[34rem] rounded-xl border border-border bg-surface p-6"
      onSubmit={(event) => {
        event.preventDefault()
        if (isValid && !isLinking) {
          onSubmit(parsed)
        }
      }}
    >
      <h4 className="font-display text-base font-bold text-foreground">
        Link existing Greenbutton Data
      </h4>
      <p className="mt-2 text-base leading-6 text-muted">{LINK_PROJECT_HELP}</p>

      <label
        className="mt-5 block font-display text-sm font-semibold text-muted"
        htmlFor="greenbutton-link-project-id"
      >
        Powur project ID
      </label>
      <input
        id="greenbutton-link-project-id"
        inputMode="numeric"
        value={projectId}
        disabled={isLinking}
        className="mt-2 h-12 w-full rounded-lg border border-border bg-background px-3 text-base text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
        onChange={(event) => setProjectId(event.target.value)}
      />

      {errorMessage ? (
        <p aria-live="polite" className="mt-3 text-sm font-semibold text-danger">
          {errorMessage}
        </p>
      ) : null}

      <div className="mt-5 flex gap-3">
        <button
          type="submit"
          aria-busy={isLinking}
          disabled={!isValid || isLinking}
          className={[
            `flex h-12 items-center justify-center rounded-xl px-5 font-display text-base font-bold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring`,
            !isValid || isLinking
              ? `cursor-not-allowed bg-surface text-muted`
              : `bg-primary text-primary-foreground hover:bg-primary/90`,
          ].join(` `)}
        >
          {isLinking ? `Linking...` : `Link project`}
        </button>
        <button
          type="button"
          disabled={isLinking}
          className="flex h-12 items-center justify-center rounded-xl border border-border px-5 font-display text-base font-bold text-muted transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

const GreenbuttonNotStartedBody: FC<GreenbuttonNotStartedBodyProps> = ({
  introCopy,
  linkForm,
  onStartLinking,
  steps,
}) => {
  return (
    <>
      <h3 className="max-w-[30rem] font-display text-2xl font-bold leading-tight text-foreground">
        {HEADLINE}
      </h3>
      <p className="mt-3 max-w-[34rem] text-base leading-7 text-muted">
        {introCopy}
      </p>

      <ol className="mt-8 space-y-6">
        {steps.map((step, index) => (
          <li key={step.title} className="flex gap-4">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-primary/20 bg-primary/5 font-display text-base font-bold text-primary">
              {index + 1}
            </span>
            <div>
              <h4 className="font-display text-base font-bold text-muted">
                {step.title}
              </h4>
              <p className="mt-1 text-base leading-6 text-muted">
                {step.description}
              </p>
            </div>
          </li>
        ))}
      </ol>

      {linkForm ?? (
        <p className="mt-8 max-w-[34rem] text-base leading-7 text-muted">
          Already have Greenbutton Data on another project for this home?{` `}
          <button
            type="button"
            className="font-semibold text-primary underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
            onClick={onStartLinking}
          >
            Link existing Greenbutton Data
          </button>
          {` `}instead of asking the homeowner again.
        </p>
      )}
    </>
  )
}

type GreenbuttonTimelineItemStatus = `complete` | `current` | `pending`

const GreenbuttonTimelineItem: FC<{
  detail: string
  index: number
  isLast?: boolean
  status: GreenbuttonTimelineItemStatus
  title: string
}> = ({ detail, index, isLast = false, status, title }) => {
  const isComplete = status === `complete`
  const isCurrent = status === `current`

  return (
    <li className="flex gap-4">
      <div className="flex w-8 shrink-0 flex-col items-center">
        <span
          className={[
            `flex size-8 items-center justify-center rounded-full border font-display text-sm font-bold`,
            isComplete
              ? `border-success bg-success text-success-foreground`
              : isCurrent
                ? `border-primary bg-primary text-primary-foreground`
                : `border-border bg-background text-muted`,
          ].join(` `)}
        >
          {isComplete ? (
            <CheckIcon aria-hidden="true" className="size-4" />
          ) : (
            index
          )}
        </span>
        {!isLast ? (
          <span className="min-h-8 flex-1 border-l border-border" />
        ) : null}
      </div>

      <div className="pb-5">
        <p
          className={[
            `font-display text-base font-bold`,
            isComplete || isCurrent ? `text-foreground` : `text-muted`,
          ].join(` `)}
        >
          {title}
        </p>
        <p className="mt-1 flex items-center gap-1.5 text-base leading-6 text-muted">
          {isCurrent ? (
            <ClockIcon aria-hidden="true" className="size-4" />
          ) : null}
          {detail}
        </p>
      </div>
    </li>
  )
}

const GreenbuttonRequestedBody: FC<{
  homeownerEmail: string
  homeownerName: string
  utilityCompanyName: string
}> = ({ homeownerEmail, homeownerName, utilityCompanyName }) => {
  return (
    <>
      <h3 className="max-w-[30rem] font-display text-2xl font-bold leading-tight text-foreground">
        Request sent to {homeownerName}
      </h3>
      <p className="mt-3 max-w-[34rem] text-base leading-7 text-muted">
        We emailed a secured connection link to {homeownerEmail}. Usage will
        appear here the moment they finish.
      </p>

      <ol className="mt-8">
        <GreenbuttonTimelineItem
          detail="Today"
          index={1}
          status="complete"
          title="Request sent"
        />
        <GreenbuttonTimelineItem
          detail="Waiting on the homeowner"
          index={2}
          status="current"
          title={`Homeowner connects ${utilityCompanyName}`}
        />
        <GreenbuttonTimelineItem
          detail="Pending"
          index={3}
          isLast
          status="pending"
          title="Usage captured for review"
        />
      </ol>
    </>
  )
}

const getMeterLabel = (
  meter: ArcadiaGreenButtonMeterSelectionOptionDto,
): string => {
  return meter.meterNumberMasked
    ? `Meter ${meter.meterNumberMasked}`
    : `Masked meter number unavailable`
}

const GreenbuttonMeterSelectionBody: FC<{
  isError: boolean
  isLoading: boolean
  meters: ArcadiaGreenButtonMeterSelectionOptionDto[]
  onSelect: (meterId: string) => void
}> = ({ isError, isLoading, meters, onSelect }) => {
  return (
    <>
      <h3 className="max-w-[30rem] font-display text-2xl font-bold leading-tight text-foreground">
        Choose the meter to use
      </h3>
      <p className="mt-3 max-w-[34rem] text-base leading-7 text-muted">
        Arcadia found more than one eligible electric meter. Choose the one that
        should provide the proposal usage history.
      </p>

      <ul className="mt-7 space-y-4">
        {meters.map((meter) => {
          const meterLabel = getMeterLabel(meter)

          return (
            <li
              key={meter.id}
              className="rounded-xl border border-border bg-surface p-5"
            >
              <p className="font-display text-lg font-bold text-foreground">
                {meter.displayLabel}
              </p>
              <p className="mt-1 text-sm font-semibold text-muted">
                {meter.providerName ?? `Utility provider`}
              </p>
              <div className="mt-2 flex flex-wrap gap-2 text-sm font-semibold text-muted">
                <span className="rounded-full border border-border bg-background px-3 py-1 capitalize">
                  {meter.serviceType ?? `Electric`} service
                </span>
                <span className="rounded-full border border-border bg-background px-3 py-1">
                  {meterLabel}
                </span>
                {meter.accountNumberMasked ? (
                  <span className="rounded-full border border-border bg-background px-3 py-1">
                    Account {meter.accountNumberMasked}
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                aria-label={`Use ${meter.displayLabel.toLowerCase()}`}
                disabled={isLoading}
                className={[
                  `mt-5 flex h-12 w-full items-center justify-center rounded-xl px-5 font-display text-base font-bold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring`,
                  isLoading
                    ? `cursor-not-allowed bg-background text-muted`
                    : `bg-primary text-primary-foreground hover:bg-primary/90`,
                ].join(` `)}
                onClick={() => onSelect(meter.id)}
              >
                {isLoading ? `Selecting...` : `Use this meter`}
              </button>
            </li>
          )
        })}
      </ul>

      {isError ? (
        <p
          aria-live="polite"
          className="mt-4 rounded-xl border border-danger/30 bg-danger/5 px-4 py-3 text-sm font-semibold text-danger"
        >
          That meter is no longer available. Choose another meter or refresh this
          data.
        </p>
      ) : null}
    </>
  )
}

const MONTH_NAMES = [
  `January`,
  `February`,
  `March`,
  `April`,
  `May`,
  `June`,
  `July`,
  `August`,
  `September`,
  `October`,
  `November`,
  `December`,
]

const formatKwh = (value: number): string =>
  `${new Intl.NumberFormat(`en-US`, { maximumFractionDigits: 1 }).format(
    value,
  )} kWh`

const IMPORT_QUALITY_LABELS: Record<
  ArcadiaGreenButtonIntervalImportDto["quality"],
  string
> = {
  annualized: `Annualized data`,
  degraded: `Degraded source data`,
  full: `Full-year data`,
  partial: `Partial data`,
}

const formatObservedDate = (value: string): string => {
  const calendarDate = value.slice(0, 10)
  return new Intl.DateTimeFormat(`en-US`, {
    day: `numeric`,
    month: `short`,
    timeZone: `UTC`,
    year: `numeric`,
  }).format(new Date(`${calendarDate}T00:00:00.000Z`))
}

const getAnnualizationLabel = (method: string): string => {
  if (method === `scale_to_365`) {
    return `Scaled to 365 days`
  }
  if (method === `seasonal_monthly_extrapolation`) {
    return `Seasonal extrapolation to full year`
  }
  return `No annualization applied`
}

const formatMissingMonths = (monthlyUsage: Array<number | null>): string =>
  monthlyUsage
    .map((value, monthIndex) =>
      value === null ? MONTH_NAMES[monthIndex] : null,
    )
    .filter((month): month is string => Boolean(month))
    .join(`, `)

const GreenbuttonConfirmationActions: FC<{
  applyLockedReason: string | null
  confirmationError: boolean
  isConfirming: boolean
  keptExisting: boolean
  modelAvailable: boolean
  onConfirm: (decision: ArcadiaGreenButtonProposalApplicationDecision) => void
  usageLocked: boolean
}> = ({
  applyLockedReason,
  confirmationError,
  isConfirming,
  keptExisting,
  modelAvailable,
  onConfirm,
  usageLocked,
}) => {
  return (
    <section className="mt-6 rounded-xl border border-primary/20 bg-primary/5 p-5">
      <h3 className="font-display text-lg font-bold text-foreground">
        Choose the proposal usage
      </h3>
      <p className="mt-2 text-sm leading-6 text-muted">
        Apply the modeled usage shown below, or keep the proposal values and
        continue with manual entry.
      </p>
      {!modelAvailable ? (
        <p className="mt-3 text-sm font-semibold text-danger">
          This model is no longer available. Keep existing usage or refresh the
          data before applying.
        </p>
      ) : usageLocked ? (
        <p className="mt-3 flex items-center gap-2 text-sm font-semibold text-foreground">
          <LockClosedIcon aria-hidden="true" className="size-4 text-warning" />
          Applying will unlock this proposal and replace its protected usage.
        </p>
      ) : null}
      {keptExisting ? (
        <div className="mt-4 rounded-lg border border-border bg-background px-4 py-3">
          <p className="font-semibold text-foreground">
            Existing proposal usage kept
          </p>
          <p className="mt-1 text-sm text-muted">
            Manual entry remains available.
          </p>
        </div>
      ) : null}
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <button
          type="button"
          className="h-11 rounded-lg bg-primary px-4 font-display text-sm font-bold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-60"
          disabled={
            isConfirming || !modelAvailable || Boolean(applyLockedReason)
          }
          onClick={() => onConfirm(`apply`)}
        >
          {isConfirming ? `Applying...` : `Apply modeled usage`}
        </button>
        <button
          type="button"
          className="h-11 rounded-lg border border-border bg-background px-4 font-display text-sm font-bold text-foreground disabled:cursor-not-allowed disabled:opacity-60"
          disabled={isConfirming || keptExisting || Boolean(applyLockedReason)}
          onClick={() => onConfirm(`keep_existing`)}
        >
          Keep existing usage
        </button>
      </div>
      {confirmationError ? (
        <p aria-live="polite" className="mt-3 text-sm font-semibold text-danger">
          We could not save that choice. Refresh the usage data and try again.
        </p>
      ) : null}
      {applyLockedReason ? (
        <p className="mt-3 text-sm font-semibold text-muted">
          {applyLockedReason}
        </p>
      ) : null}
    </section>
  )
}

const GreenbuttonCapturedBody: FC<{
  applied: boolean
  applyLockedReason?: string | null
  applicationReason: ArcadiaGreenButtonProposalApplicationReason | null
  children?: ReactNode
  confirmationError: boolean
  currentImport: ArcadiaGreenButtonIntervalImportDto
  hasRefreshFailure: boolean
  isProposalRefreshFailed: boolean
  isConfirming: boolean
  isResumingUsage: boolean
  householdApplyBlockedReason?: ConsumptionSelectionBlockedReason | null
  onRefreshProposal: VoidFunction
  onConfirm: (decision: ArcadiaGreenButtonProposalApplicationDecision) => void
  onResumeUsage: VoidFunction
  usageLocked: boolean
}> = ({
  applied,
  applyLockedReason = null,
  applicationReason,
  children,
  confirmationError,
  currentImport,
  hasRefreshFailure,
  isProposalRefreshFailed,
  isConfirming,
  isResumingUsage,
  householdApplyBlockedReason = null,
  onRefreshProposal,
  onConfirm,
  onResumeUsage,
  usageLocked,
}) => {
  const coverage =
    currentImport.coverageDays === null
      ? `Coverage unavailable`
      : `${currentImport.coverageDays.toLocaleString()}-day source interval window`
  const quality = IMPORT_QUALITY_LABELS[currentImport.quality]
  const requiresImportConfirmation =
    currentImport.quality === `partial` || currentImport.quality === `annualized`
  const isDegradedImport = currentImport.quality === `degraded`
  const observedPeriod =
    currentImport.firstIntervalAt && currentImport.lastIntervalAt
      ? `${formatObservedDate(
          currentImport.firstIntervalAt,
        )} - ${formatObservedDate(currentImport.lastIntervalAt)}`
      : `Observed dates unavailable`
  const observedMonthlyEnergyKwh =
    currentImport.observedMonthlyEnergyKwh ??
    (requiresImportConfirmation
      ? Array(12).fill(null)
      : currentImport.observedMonthlyKwh)
  const annualEnergyKwh = requiresImportConfirmation
    ? currentImport.modeledAnnualEnergyKwh
    : currentImport.annualKwh
  const monthlyEnergyKwh = requiresImportConfirmation
    ? currentImport.modeledMonthlyEnergyKwh
    : currentImport.monthlyKwh
  const missingMonths = formatMissingMonths(observedMonthlyEnergyKwh)

  return (
    <>
      <div className="rounded-xl border border-success/20 bg-success/5 p-5">
        <p className="font-display text-sm font-bold tracking-[0.12em] text-success uppercase">
          Ready to review
        </p>
        <p className="mt-2 font-display text-3xl font-bold tabular-nums text-foreground">
          {annualEnergyKwh === null
            ? `Model unavailable`
            : formatKwh(annualEnergyKwh)}
        </p>
        <p className="mt-1 text-base font-semibold text-muted">
          {requiresImportConfirmation
            ? `Proposed modeled annual usage`
            : `Annual captured usage`}
        </p>
        <p
          className={[
            `mt-4 inline-flex rounded-full border px-3 py-1 text-sm font-semibold`,
            applied
              ? `border-success/20 bg-success/5 text-success`
              : `border-warning/40 bg-warning/15 text-foreground`,
          ].join(` `)}
        >
          {applied
            ? `Applied to proposal consumption`
            : `Not applied to proposal consumption`}
        </p>
      </div>

      {hasRefreshFailure ? (
        <p className="mt-4 rounded-xl border border-warning/40 bg-warning/15 px-4 py-3 text-sm font-semibold text-foreground">
          The latest refresh failed. Previously captured usage remains available.
        </p>
      ) : null}

      {householdApplyBlockedReason === `grid_import_basis` ? (
        <div className="mt-4 rounded-xl border border-warning/40 bg-warning/15 px-4 py-3">
          <p className="text-sm font-semibold text-foreground">
            {GRID_IMPORT_CONNECTED_COPY}
          </p>
        </div>
      ) : usageLocked && !requiresImportConfirmation && !isDegradedImport ? (
        <div className="mt-4 rounded-xl border border-warning/40 bg-warning/15 px-4 py-3">
          <p className="text-sm font-semibold text-foreground">
            Manual proposal usage is protected from Green Button updates.
          </p>
          <button
            type="button"
            className="mt-3 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isResumingUsage || Boolean(applyLockedReason)}
            onClick={onResumeUsage}
          >
            {isResumingUsage ? `Resuming...` : `Use Green Button usage`}
          </button>
          {applyLockedReason ? (
            <p className="mt-2 text-sm font-semibold text-muted">
              {applyLockedReason}
            </p>
          ) : null}
        </div>
      ) : null}

      {requiresImportConfirmation && !applied ? (
        <GreenbuttonConfirmationActions
          applyLockedReason={applyLockedReason}
          confirmationError={confirmationError}
          isConfirming={isConfirming}
          keptExisting={applicationReason === `seller_declined`}
          modelAvailable={Boolean(
            currentImport.modeledProfileId &&
              currentImport.modeledAnnualEnergyKwh &&
              currentImport.modeledMonthlyEnergyKwh.length === 12,
          )}
          onConfirm={onConfirm}
          usageLocked={usageLocked}
        />
      ) : null}

      {isProposalRefreshFailed ? (
        <div className="mt-4 rounded-xl border border-warning/40 bg-warning/15 px-4 py-3">
          <p className="text-sm font-semibold text-foreground">
            Usage was applied, but the visible proposal values could not refresh.
          </p>
          <button
            type="button"
            className="mt-3 rounded-lg border border-border bg-background px-4 py-2 text-sm font-bold text-foreground"
            onClick={onRefreshProposal}
          >
            Refresh proposal values
          </button>
        </div>
      ) : null}

      {children}

      <div className="mt-6 flex flex-wrap gap-2 text-sm font-semibold text-muted">
        <span className="rounded-full border border-border bg-surface px-3 py-1">
          {quality}
        </span>
        <span className="rounded-full border border-border bg-surface px-3 py-1">
          {coverage}
        </span>
        <span className="rounded-full border border-border bg-surface px-3 py-1">
          {currentImport.imputedIntervalCount.toLocaleString()} imputed intervals
        </span>
      </div>

      <dl className="mt-5 grid grid-cols-1 gap-3 rounded-xl border border-border bg-surface p-4 sm:grid-cols-2">
        <div>
          <dt className="text-xs font-bold tracking-wide text-muted uppercase">
            Observed period
          </dt>
          <dd className="mt-1 font-semibold text-foreground">
            {observedPeriod}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-bold tracking-wide text-muted uppercase">
            Observed usage
          </dt>
          <dd className="mt-1 font-semibold text-foreground">
            {currentImport.observedAnnualKwh === null
              ? `Unavailable`
              : `${formatKwh(currentImport.observedAnnualKwh)} observed`}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-bold tracking-wide text-muted uppercase">
            Annualization
          </dt>
          <dd className="mt-1 font-semibold text-foreground">
            {getAnnualizationLabel(
              currentImport.modelingMethod ?? currentImport.annualizationMethod,
            )}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-bold tracking-wide text-muted uppercase">
            Observed months
          </dt>
          <dd className="mt-1 font-semibold text-foreground">
            {currentImport.observedMonthCount === null
              ? `Unavailable`
              : `${currentImport.observedMonthCount} of 12`}
          </dd>
        </div>
      </dl>

      {requiresImportConfirmation ? (
        <p className="mt-4 rounded-xl border border-warning/40 bg-warning/15 px-4 py-3 text-sm font-semibold text-foreground">
          {missingMonths
            ? `Missing periods: ${missingMonths}. Vision modeled these periods from the observed window.`
            : `Vision modeled a complete year from the observed window.`}
        </p>
      ) : null}

      <section className="mt-7" aria-labelledby="captured-monthly-usage">
        <h3
          id="captured-monthly-usage"
          className="font-display text-lg font-bold text-foreground"
        >
          {requiresImportConfirmation
            ? `Proposed modeled monthly usage`
            : `Monthly captured usage`}
        </h3>
        <dl className="mt-3 grid grid-cols-1 overflow-hidden rounded-xl border border-border sm:grid-cols-2">
          {MONTH_NAMES.map((month, index) => (
            <div
              key={month}
              className="flex items-center justify-between gap-4 border-b border-border px-4 py-3 last:border-b-0 sm:odd:border-r"
            >
              <dt className="font-semibold text-muted">{month}</dt>
              <dd className="font-display font-bold tabular-nums text-foreground">
                {monthlyEnergyKwh[index] === undefined
                  ? `Unavailable`
                  : formatKwh(monthlyEnergyKwh[index])}
                {observedMonthlyEnergyKwh[index] === null ? (
                  <span className="mt-0.5 block font-sans text-xs font-semibold text-muted">
                    Not observed
                  </span>
                ) : observedMonthlyEnergyKwh[index] !== undefined &&
                  observedMonthlyEnergyKwh[index] !== monthlyEnergyKwh[index] ? (
                  <span className="mt-0.5 block font-sans text-xs font-semibold text-muted">
                    {formatKwh(observedMonthlyEnergyKwh[index] ?? 0)}
                    {` `}
                    observed
                  </span>
                ) : null}
              </dd>
            </div>
          ))}
        </dl>
      </section>
    </>
  )
}

export const LINKED_CONNECTION_NOTE = `Linked from another Powur project for this home - the homeowner was not asked to connect again.`

const GreenbuttonDataHeader: FC<{
  homeownerName: string
  isLinkedConnection: boolean
  status: GreenbuttonDisplayStatus
  utilityCompanyName: string
}> = ({ homeownerName, isLinkedConnection, status, utilityCompanyName }) => {
  const statusConfig = GREENBUTTON_STATUS_CONFIG[status]

  return (
    <>
      <header className="border-b border-border bg-background px-8 py-6">
        <div className="min-w-0">
          <p className="font-display text-sm font-bold tracking-[0.18em] text-muted uppercase">
            Energy Consumption Data
          </p>
          <div className="mt-2 flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
            <h2 className="font-display text-2xl font-bold leading-tight text-foreground">
              Homeowner utility data
            </h2>
            <Badge tone="yellow" variant="flat">
              Beta
            </Badge>
          </div>
          <p className="mt-1 text-lg text-muted">
            {utilityCompanyName} - {homeownerName}
          </p>
        </div>
      </header>

      <section className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-border bg-background px-8 py-3.5">
        <div
          className={[
            `inline-flex items-center gap-2 rounded-full border px-3 py-1.5 font-display text-sm font-semibold shadow-sm`,
            statusConfig.pillClassName,
          ].join(` `)}
        >
          <span
            className={[`size-2.5 rounded-full`, statusConfig.dotClassName].join(
              ` `,
            )}
          />
          {statusConfig.label}
        </div>
        <p className="text-base font-semibold text-muted">
          {statusConfig.description}
        </p>
        {isLinkedConnection ? (
          <p className="basis-full text-sm font-semibold text-muted">
            {LINKED_CONNECTION_NOTE}
          </p>
        ) : null}
      </section>
    </>
  )
}

/**
 * A connection whose import is running on its own, with no homeowner or meter
 * step pending: the only state V2's resume accepts as a pending import.
 */
const RUNNING_IMPORT_CONNECTION_STATUSES: ReadonlySet<string> = new Set([
  `credentials_submitted`,
  `discovering`,
  `activating_meter`,
  `intervals_pending`,
  `importing`,
])

/**
 * Selection-only mode's own status: what the last apply changed, and while
 * the import is still running, a way to release a manual lock so Green
 * Button applies when it finishes.
 */
const SelectionOnlyStatus: FC<{
  applyLockedReason: string | null
  canResumePendingImport: boolean
  isResuming: boolean
  notice: `applied` | `pending_import` | null
  onRecalculateSavings?: () => void
  onResume: VoidFunction
  resumeError: string | null
}> = ({
  applyLockedReason,
  canResumePendingImport,
  isResuming,
  notice,
  onRecalculateSavings,
  onResume,
  resumeError,
}) => (
  <>
    {notice === `applied` ? (
      <div
        role="status"
        className="mb-6 rounded-xl border border-success/20 bg-success/5 px-4 py-3"
        data-testid="greenbutton-selection-only-applied"
      >
        <p className="text-sm font-semibold text-foreground">
          {SELECTION_ONLY_APPLIED_COPY}
        </p>
        {onRecalculateSavings ? (
          <button
            type="button"
            className="mt-2 text-sm font-semibold text-primary underline"
            onClick={onRecalculateSavings}
          >
            Recalculate savings
          </button>
        ) : null}
      </div>
    ) : notice === `pending_import` ? (
      <p
        role="status"
        className="mb-6 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm font-semibold text-foreground"
        data-testid="greenbutton-selection-only-pending"
      >
        {SELECTION_ONLY_PENDING_IMPORT_COPY}
      </p>
    ) : canResumePendingImport ? (
      <div className="mb-6 rounded-xl border border-warning/40 bg-warning/15 px-4 py-3">
        <p className="text-sm font-semibold text-foreground">
          Manual proposal usage is protected from Green Button updates.
        </p>
        <button
          type="button"
          className="mt-3 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-60"
          disabled={isResuming || Boolean(applyLockedReason)}
          onClick={onResume}
        >
          {isResuming
            ? `Resuming...`
            : `Use Green Button when the import finishes`}
        </button>
        {applyLockedReason ? (
          <p className="mt-2 text-sm font-semibold text-muted">
            {applyLockedReason}
          </p>
        ) : null}
      </div>
    ) : null}
    {resumeError ? (
      <p aria-live="polite" className="mb-6 text-sm font-semibold text-danger">
        {resumeError}
      </p>
    ) : null}
  </>
)

export const GreenbuttonDataDrawer: FC<GreenbuttonDataDrawerProps> = ({
  applyLockedReason = null,
  applyMode = `v1`,
  canUseSupportDiagnostics = false,
  forBatteryOnlyProposal = false,
  afterCapturedUsage,
  homeownerEmail,
  homeownerName,
  onClose,
  onRecalculateSavings,
  onUsageApplied,
  proposalId,
  siteId,
  theme = `light`,
  utilityCompanyName,
}) => {
  const queryClient = useQueryClient()
  const startConnection = useStartArcadiaGreenButtonConnection()
  const confirmUsage = useConfirmArcadiaGreenButtonUsage()
  const resendConnectionEmail = useResendArcadiaGreenButtonConnectionEmail()
  const retryConnection = useRetryArcadiaGreenButtonConnection()
  const v1ResumeUsage = useResumeArcadiaGreenButtonUsage()
  const selectionOnlyApply = useApplySolarProposalGreenButton()
  const selectionOnly = applyMode === `selection_only`
  // Switch, resume and confirm each apply data to the proposal. V1 and Battery
  // recalculate through V1's endpoints; selection-only mode calls V2's, which
  // answer at once, so its notices say what changed instead.
  const resumeUsage = selectionOnly ? selectionOnlyApply : v1ResumeUsage
  const confirmation = selectionOnly ? selectionOnlyApply : confirmUsage
  const [selectionOnlyNotice, setSelectionOnlyNotice] = useState<
    `applied` | `pending_import` | null
  >(null)
  const effectiveApplyLockedReason = selectionOnly ? applyLockedReason : null
  const selectionOnlyFailed = (action: `confirm` | `resume`): boolean =>
    selectionOnlyApply.isError && selectionOnlyApply.variables?.action === action
  const selectMeter = useSelectArcadiaGreenButtonMeter()
  const linkProject = useLinkArcadiaGreenButtonProject()
  const [isLinkingProject, setIsLinkingProject] = useState(false)
  const consumptionSelection = useProposalConsumptionSelection(proposalId)
  const retryInFlightRef = useRef(false)
  const retryRequestRef = useRef<{
    connectionId: string
    failedAt: string | null
    requestId: string
  } | null>(null)
  const [copyStatus, setCopyStatus] = useState<`copied` | `idle`>(`idle`)
  const greenbuttonSiteData = useFetchArcadiaGreenButtonSiteData(
    siteId,
    proposalId,
    { enabled: Boolean(siteId) },
  )
  const consumptionInsights = useProposalConsumptionInsights(proposalId, {
    enabled: greenbuttonSiteData.isSuccess,
    importId: greenbuttonSiteData.data?.currentImport?.id,
  })
  const [confirmedAppliedImportId, setConfirmedAppliedImportId] = useState<
    string | null
  >(null)
  const [isProposalRefreshFailed, setIsProposalRefreshFailed] = useState(false)
  const [activeTab, setActiveTab] = useState<GreenbuttonDrawerTabId>(`overview`)
  const appliedImportId =
    greenbuttonSiteData.data?.currentProposalApplication?.outcome === `applied`
      ? (greenbuttonSiteData.data.currentImport?.id ?? null)
      : null

  const typicalDayAvailable =
    consumptionInsights.data?.typicalDay.available === true
  const usagePatternsAvailable =
    consumptionInsights.data?.usagePatterns.available === true
  const solarFitAvailable = consumptionInsights.data?.solarFit.available === true
  const drawerTabs = useMemo<readonly GreenbuttonDrawerTab[]>(() => {
    const baseTabs = GREENBUTTON_DRAWER_TABS.map((tab) => {
      if (tab.id === `typicalDay`) {
        return { ...tab, needsMoreData: !typicalDayAvailable }
      }
      if (tab.id === `usagePatterns`) {
        return { ...tab, needsMoreData: !usagePatternsAvailable }
      }
      if (tab.id === `solarFit`) {
        return { ...tab, needsMoreData: !solarFitAvailable }
      }
      return tab
    })

    return canUseSupportDiagnostics
      ? [...baseTabs, GREENBUTTON_SUPPORT_TAB]
      : baseTabs
  }, [
    canUseSupportDiagnostics,
    solarFitAvailable,
    typicalDayAvailable,
    usagePatternsAvailable,
  ])
  const visibleActiveTab = drawerTabs.some((tab) => tab.id === activeTab)
    ? activeTab
    : `overview`

  const refreshVisibleProposalUsage = useCallback(async () => {
    if (!appliedImportId || !onUsageApplied) {
      return
    }

    setIsProposalRefreshFailed(false)
    try {
      await onUsageApplied()
      await queryClient.invalidateQueries({
        queryKey: queryKeys.proposalConsumption.insights(proposalId),
      })
      setConfirmedAppliedImportId(appliedImportId)
    } catch {
      setIsProposalRefreshFailed(true)
    }
  }, [appliedImportId, onUsageApplied, proposalId, queryClient])

  useEffect(() => {
    if (confirmedAppliedImportId !== appliedImportId) {
      void refreshVisibleProposalUsage()
    }
  }, [appliedImportId, confirmedAppliedImportId, refreshVisibleProposalUsage])

  // The comparison's candidates come from a read that rarely refetches; only
  // the Site read polls. When it shows a new import, refetch the candidates so
  // selection-only mode can offer data no automatic application will apply.
  const siteImportId = greenbuttonSiteData.data
    ? (greenbuttonSiteData.data.currentImport?.id ?? null)
    : undefined
  const seenSiteImportIdRef = useRef(siteImportId)
  useEffect(() => {
    if (siteImportId === undefined) {
      return
    }
    const previous = seenSiteImportIdRef.current
    seenSiteImportIdRef.current = siteImportId
    if (selectionOnly && previous !== undefined && previous !== siteImportId) {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.proposalConsumption.selection(proposalId),
        exact: true,
      })
    }
  }, [proposalId, queryClient, selectionOnly, siteImportId])
  const currentConnection =
    greenbuttonSiteData.data?.connection ?? startConnection.data?.connection
  const displayStatus = getGreenbuttonDisplayStatus({
    connection: currentConnection,
    currentImport: greenbuttonSiteData.data?.currentImport,
    currentProposalApplicationOutcome:
      confirmedAppliedImportId === appliedImportId
        ? greenbuttonSiteData.data?.currentProposalApplication?.outcome
        : null,
    isCurrentProposalUsageLocked:
      greenbuttonSiteData.data?.isCurrentProposalUsageLocked ?? false,
    hasMeterSelectionOptions:
      (greenbuttonSiteData.data?.meterSelectionOptions.length ?? 0) > 1,
  })
  const introCopy = `Request 12 months of actual usage from ${utilityCompanyName}. Captured usage will be ready to review before it changes this proposal.`
  const steps = buildSteps(homeownerName)
  const connectUrl =
    greenbuttonSiteData.data?.connectUrl ??
    startConnection.data?.connectUrl ??
    null
  const shouldShowCopyLink = displayStatus !== `not-started`
  const shouldShowResendEmail = displayStatus === `requested`
  const shouldShowRetry =
    displayStatus === `failed` || Boolean(currentConnection?.failedAt)
  const copyConnectionLinkDisabled = !connectUrl
  const currentConnectionId = currentConnection?.id ?? null
  const hasSite = Boolean(siteId)
  const hasProposal = Boolean(proposalId)
  const hasCurrentConnection = Boolean(currentConnectionId)
  const hasResendEmailContext = hasSite && hasProposal && hasCurrentConnection
  const resendEmailInFlight = resendConnectionEmail.isLoading
  const resendEmailDisabled = !hasResendEmailContext || resendEmailInFlight
  const resendEmailLabel = resendConnectionEmail.isLoading
    ? `Resending...`
    : `Resend email`
  const resendEmailStatus = getResendEmailStatus({
    homeownerEmail,
    isError: resendConnectionEmail.isError,
    isSuccess: resendConnectionEmail.isSuccess,
  })
  const retryDisabled =
    !hasSite ||
    !hasProposal ||
    !hasCurrentConnection ||
    retryConnection.isLoading
  const hasSendRequestContext = hasSite && hasProposal
  const sendRequestInFlight = startConnection.isLoading
  const sendRequestDisabled = !hasSendRequestContext || sendRequestInFlight
  const sendRequestLabel = getSendRequestLabel({
    isLoading: startConnection.isLoading,
    isSuccess: startConnection.isSuccess,
  })
  const sendRequestStatus = getSendRequestStatus({
    hasProposal,
    hasSite,
    isError: startConnection.isError,
    isSuccess: startConnection.isSuccess,
  })

  const handleSendRequest = () => {
    if (sendRequestDisabled) {
      return
    }

    startConnection.mutate({ proposalId, siteId })
  }

  const handleResendEmail = () => {
    if (resendEmailDisabled || !currentConnectionId) {
      return
    }

    resendConnectionEmail.mutate({
      connectionId: currentConnectionId,
      proposalId,
      siteId,
    })
  }

  const handleCopyConnectionLink = async () => {
    if (!connectUrl) {
      return
    }

    await navigator.clipboard.writeText(connectUrl)
    setCopyStatus(`copied`)
    window.setTimeout(() => setCopyStatus(`idle`), 1500)
  }

  const handleRetry = () => {
    if (retryDisabled || retryInFlightRef.current || !currentConnectionId) {
      return
    }

    retryInFlightRef.current = true
    const request =
      retryRequestRef.current?.connectionId === currentConnectionId
        ? retryRequestRef.current
        : {
            connectionId: currentConnectionId,
            failedAt: currentConnection?.failedAt ?? null,
            requestId: crypto.randomUUID(),
          }
    retryRequestRef.current = request
    retryConnection.mutate(
      {
        connectionId: currentConnectionId,
        proposalId,
        requestId: request.requestId,
        siteId,
      },
      {
        onError: (error) => {
          if (
            isAxiosError(error) &&
            error.response &&
            error.response.status >= 400 &&
            error.response.status < 500
          ) {
            retryRequestRef.current = null
          }
        },
        onSuccess: (connection) => {
          if (
            connection.status !== `failed` ||
            connection.failedAt !== request.failedAt
          ) {
            retryRequestRef.current = null
          }
        },
        onSettled: () => {
          retryInFlightRef.current = false
        },
      },
    )
  }

  const handleSelectMeter = (meterId: string) => {
    if (!currentConnectionId || !proposalId || selectMeter.isLoading) {
      return
    }

    selectMeter.mutate({
      connectionId: currentConnectionId,
      meterId,
      proposalId,
      siteId,
    })
  }

  const handleResumeUsage = () => {
    if (
      !siteId ||
      !proposalId ||
      resumeUsage.isLoading ||
      effectiveApplyLockedReason
    ) {
      return
    }

    setConfirmedAppliedImportId(null)
    if (selectionOnly) {
      setSelectionOnlyNotice(null)
      selectionOnlyApply.mutate(
        { proposalId, siteId, action: `resume`, body: {} },
        {
          onSuccess: ({ pendingImport }) =>
            setSelectionOnlyNotice(pendingImport ? `pending_import` : `applied`),
        },
      )
      return
    }
    v1ResumeUsage.mutate({ proposalId, siteId })
  }

  const handleSelectionOnlySwitch = useCallback(async () => {
    setSelectionOnlyNotice(`applied`)
    await onUsageApplied?.()
  }, [onUsageApplied])

  const handleConfirmUsage = (
    decision: ArcadiaGreenButtonProposalApplicationDecision,
  ) => {
    const importId = greenbuttonSiteData.data?.currentImport?.id
    const modeledProfileId =
      greenbuttonSiteData.data?.currentImport?.modeledProfileId
    if (
      !siteId ||
      !proposalId ||
      !importId ||
      (decision === `apply` && !modeledProfileId) ||
      confirmation.isLoading
    ) {
      return
    }

    if (decision === `apply`) {
      setConfirmedAppliedImportId(null)
    }
    if (selectionOnly) {
      if (effectiveApplyLockedReason) {
        return
      }
      setSelectionOnlyNotice(null)
      selectionOnlyApply.mutate(
        {
          proposalId,
          siteId,
          action: `confirm`,
          body: {
            decision,
            importId,
            ...(modeledProfileId ? { modeledProfileId } : {}),
          },
        },
        {
          onSuccess: () => {
            if (decision === `apply`) {
              setSelectionOnlyNotice(`applied`)
            }
          },
        },
      )
      return
    }
    confirmUsage.mutate({
      decision,
      importId,
      ...(modeledProfileId ? { modeledProfileId } : {}),
      proposalId,
      siteId,
    })
  }

  return (
    <Drawer
      open
      onClose={onClose}
      theme={theme}
      title="Energy Consumption Data"
      titleSrOnly
      widthClass="max-w-[calc(100vw-4rem)]"
      contentClassName="!mt-0 flex min-h-0 flex-1 flex-col bg-background !px-0 sm:!px-0"
      header={() => (
        <GreenbuttonDataHeader
          homeownerName={homeownerName}
          isLinkedConnection={Boolean(
            greenbuttonSiteData.data?.sharedFromSiteId,
          )}
          status={displayStatus}
          utilityCompanyName={utilityCompanyName}
        />
      )}
    >
      <GreenbuttonDrawerTabBar
        activeTab={visibleActiveTab}
        onTabChange={setActiveTab}
        tabs={drawerTabs}
      />
      <main className="min-h-0 flex-1 overflow-y-auto px-8 py-7">
        {visibleActiveTab === `overview` ? (
          <>
            {selectionOnly ? (
              <SelectionOnlyStatus
                applyLockedReason={effectiveApplyLockedReason}
                canResumePendingImport={
                  // Only this Site's own running import applies to the
                  // proposal when it finishes; a linked project's never does.
                  displayStatus === `connecting` &&
                  RUNNING_IMPORT_CONNECTION_STATUSES.has(
                    currentConnection?.status ?? ``,
                  ) &&
                  Boolean(
                    greenbuttonSiteData.data?.isCurrentProposalUsageLocked,
                  ) &&
                  !greenbuttonSiteData.data?.currentImport &&
                  !greenbuttonSiteData.data?.sharedFromSiteId
                }
                isResuming={resumeUsage.isLoading}
                notice={
                  // A pending import settles once the Site read shows its
                  // import, or once the connection stops importing (failed,
                  // cancelled, expired); the drawer's status takes over then.
                  selectionOnlyNotice === `pending_import` &&
                  (greenbuttonSiteData.data?.currentImport ||
                    !RUNNING_IMPORT_CONNECTION_STATUSES.has(
                      currentConnection?.status ?? ``,
                    ))
                    ? null
                    : selectionOnlyNotice
                }
                onRecalculateSavings={onRecalculateSavings}
                onResume={handleResumeUsage}
                resumeError={
                  selectionOnlyFailed(`resume`)
                    ? parseAxiosError(
                        selectionOnlyApply.error,
                        `Unable to apply Green Button usage. Please try again.`,
                      )
                    : null
                }
              />
            ) : null}
            {(displayStatus === `captured` || displayStatus === `synced`) &&
            greenbuttonSiteData.data?.currentImport ? (
              <GreenbuttonCapturedBody
                applied={displayStatus === `synced`}
                applyLockedReason={effectiveApplyLockedReason}
                applicationReason={
                  greenbuttonSiteData.data.currentProposalApplication?.reason ??
                  null
                }
                confirmationError={
                  selectionOnly
                    ? selectionOnlyFailed(`confirm`)
                    : confirmUsage.isError
                }
                currentImport={greenbuttonSiteData.data.currentImport}
                hasRefreshFailure={Boolean(currentConnection?.failedAt)}
                isConfirming={
                  confirmation.isLoading ||
                  (greenbuttonSiteData.data.currentProposalApplication
                    ?.confirmationDecision === `apply` &&
                    greenbuttonSiteData.data.currentProposalApplication
                      .outcome === `processing`)
                }
                isProposalRefreshFailed={isProposalRefreshFailed}
                isResumingUsage={resumeUsage.isLoading}
                householdApplyBlockedReason={
                  forBatteryOnlyProposal
                    ? (consumptionSelection.data?.candidates.find(
                        (candidate) => candidate.sourceSystem === `ARCADIA_PLUG`,
                      )?.selectionBlockedReason ??
                      (greenbuttonSiteData.data?.currentProposalApplication
                        ?.reason === `grid_import_basis`
                        ? `grid_import_basis`
                        : null))
                    : null
                }
                onConfirm={handleConfirmUsage}
                onRefreshProposal={() => void refreshVisibleProposalUsage()}
                onResumeUsage={handleResumeUsage}
                usageLocked={
                  greenbuttonSiteData.data.isCurrentProposalUsageLocked
                }
              >
                <ConsumptionSourceComparison
                  applyLockedReason={effectiveApplyLockedReason}
                  applyMode={applyMode}
                  hideBlockedHouseholdApply={forBatteryOnlyProposal}
                  onSelectionApplied={
                    selectionOnly ? handleSelectionOnlySwitch : onUsageApplied
                  }
                  proposalId={proposalId}
                  siteId={siteId}
                />
                {afterCapturedUsage}
              </GreenbuttonCapturedBody>
            ) : displayStatus === `requested` ? (
              <GreenbuttonRequestedBody
                homeownerEmail={homeownerEmail}
                homeownerName={homeownerName}
                utilityCompanyName={utilityCompanyName}
              />
            ) : displayStatus === `selection-required` ? (
              <GreenbuttonMeterSelectionBody
                isError={selectMeter.isError}
                isLoading={selectMeter.isLoading}
                meters={greenbuttonSiteData.data?.meterSelectionOptions ?? []}
                onSelect={handleSelectMeter}
              />
            ) : (
              <GreenbuttonNotStartedBody
                introCopy={introCopy}
                linkForm={
                  isLinkingProject ? (
                    <GreenbuttonLinkProjectForm
                      errorMessage={
                        linkProject.isError
                          ? parseAxiosError(
                              linkProject.error,
                              `Unable to link that project. Please try again.`,
                            )
                          : null
                      }
                      isLinking={linkProject.isLoading}
                      onCancel={() => {
                        linkProject.reset()
                        setIsLinkingProject(false)
                      }}
                      onSubmit={(powurProjectId) =>
                        linkProject.mutate(
                          { powurProjectId, proposalId, siteId },
                          { onSuccess: () => setIsLinkingProject(false) },
                        )
                      }
                    />
                  ) : null
                }
                onStartLinking={() => setIsLinkingProject(true)}
                steps={steps}
              />
            )}
          </>
        ) : null}
        {visibleActiveTab === `typicalDay` ? (
          consumptionInsights.data?.typicalDay.available === true ? (
            <TypicalDayTab
              viewModel={consumptionInsights.data.typicalDay.data}
            />
          ) : (
            <TypicalDayEmptyState
              evidence={
                consumptionInsights.data?.typicalDay.available === false
                  ? consumptionInsights.data.typicalDay.evidence
                  : undefined
              }
              reason={
                consumptionInsights.data?.typicalDay.available === false
                  ? consumptionInsights.data.typicalDay.reason
                  : undefined
              }
            />
          )
        ) : null}
        {visibleActiveTab === `usagePatterns` ? (
          consumptionInsights.data?.usagePatterns.available === true ? (
            <UsagePatternsTab
              viewModel={consumptionInsights.data.usagePatterns.data}
            />
          ) : (
            <UsagePatternsEmptyState
              evidence={
                consumptionInsights.data?.usagePatterns.available === false
                  ? consumptionInsights.data.usagePatterns.evidence
                  : undefined
              }
              reason={
                consumptionInsights.data?.usagePatterns.available === false
                  ? consumptionInsights.data.usagePatterns.reason
                  : undefined
              }
            />
          )
        ) : null}
        {visibleActiveTab === `solarFit` ? (
          consumptionInsights.data?.solarFit.available === true ? (
            <SolarFitTab viewModel={consumptionInsights.data.solarFit.data} />
          ) : (
            <SolarFitEmptyState
              evidence={
                consumptionInsights.data?.solarFit.available === false
                  ? consumptionInsights.data.solarFit.evidence
                  : undefined
              }
              reason={
                consumptionInsights.data?.solarFit.available === false
                  ? consumptionInsights.data.solarFit.reason
                  : undefined
              }
            />
          )
        ) : null}
        {visibleActiveTab === `support` ? (
          currentConnectionId ? (
            <GreenbuttonSupportDiagnostics
              connectionId={currentConnectionId}
              siteId={siteId}
            />
          ) : (
            <p className="text-sm font-semibold text-muted">
              Support diagnostics need an active Green Button connection.
            </p>
          )
        ) : null}
      </main>

      <footer className="w-full shrink-0 border-t border-border bg-background">
        <div className="px-8 py-5">
          {shouldShowCopyLink ? (
            <>
              <div
                className={
                  shouldShowResendEmail || shouldShowRetry
                    ? `grid grid-cols-1 gap-3 sm:grid-cols-2`
                    : ``
                }
              >
                <button
                  type="button"
                  disabled={copyConnectionLinkDisabled}
                  className={[
                    `flex h-14 w-full items-center justify-center gap-3 rounded-xl border px-5 font-display text-base font-bold shadow-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring`,
                    copyConnectionLinkDisabled
                      ? `cursor-not-allowed border-border bg-surface text-muted shadow-none`
                      : `border-primary/30 bg-background text-primary hover:bg-primary/5`,
                  ].join(` `)}
                  onClick={handleCopyConnectionLink}
                >
                  <ClipboardDocumentIcon aria-hidden="true" className="size-6" />
                  {copyStatus === `copied` ? `Copied link` : `Copy link`}
                </button>
                {shouldShowResendEmail ? (
                  <button
                    type="button"
                    aria-busy={resendConnectionEmail.isLoading}
                    disabled={resendEmailDisabled}
                    className={[
                      `flex h-14 w-full items-center justify-center gap-3 rounded-xl px-5 font-display text-base font-bold shadow-md transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring`,
                      resendEmailDisabled
                        ? `cursor-not-allowed bg-surface text-muted shadow-none`
                        : `bg-primary text-primary-foreground hover:bg-primary/90`,
                    ].join(` `)}
                    onClick={handleResendEmail}
                  >
                    <EnvelopeIcon aria-hidden="true" className="size-6" />
                    {resendEmailLabel}
                  </button>
                ) : shouldShowRetry ? (
                  <button
                    type="button"
                    aria-busy={retryConnection.isLoading}
                    disabled={retryDisabled}
                    className={[
                      `flex h-14 w-full items-center justify-center gap-3 rounded-xl px-5 font-display text-base font-bold shadow-md transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring`,
                      retryDisabled
                        ? `cursor-not-allowed bg-surface text-muted shadow-none`
                        : `bg-primary text-primary-foreground hover:bg-primary/90`,
                    ].join(` `)}
                    onClick={handleRetry}
                  >
                    <ArrowPathIcon aria-hidden="true" className="size-6" />
                    {retryConnection.isLoading ? `Retrying...` : `Try again`}
                  </button>
                ) : null}
              </div>
              {shouldShowResendEmail && resendEmailStatus ? (
                <p
                  aria-live="polite"
                  className="mt-3 text-center text-sm font-semibold text-muted"
                >
                  {resendEmailStatus}
                </p>
              ) : null}
              {shouldShowRetry && retryConnection.isError ? (
                <p
                  aria-live="polite"
                  className="mt-3 text-center text-sm font-semibold text-danger"
                >
                  {parseAxiosError(
                    retryConnection.error,
                    `Unable to retry collection. Please try again.`,
                  )}
                </p>
              ) : null}
            </>
          ) : (
            <>
              <button
                type="button"
                aria-busy={startConnection.isLoading}
                disabled={sendRequestDisabled}
                className={[
                  `flex h-14 w-full items-center justify-center gap-3 rounded-xl px-5 font-display text-lg font-bold shadow-md transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring`,
                  sendRequestDisabled
                    ? `cursor-not-allowed bg-surface text-muted shadow-none`
                    : `bg-primary text-primary-foreground hover:bg-primary/90`,
                ].join(` `)}
                onClick={handleSendRequest}
              >
                <EnvelopeIcon aria-hidden="true" className="size-6" />
                {sendRequestLabel}
              </button>
              {sendRequestStatus ? (
                <p
                  aria-live="polite"
                  className="mt-3 text-center text-sm font-semibold text-muted"
                >
                  {sendRequestStatus}
                </p>
              ) : null}
            </>
          )}
        </div>
        <div className="flex min-h-12 items-center justify-between gap-4 border-t border-border bg-surface px-8 py-3 text-sm font-semibold text-muted">
          <div className="flex items-center gap-2">
            <LockClosedIcon className="size-4" />
            <span>Secure, read-only connection</span>
          </div>
          <div>
            Powered by <span className="text-foreground">Arcadia</span>
          </div>
        </div>
      </footer>
    </Drawer>
  )
}
